import axios from 'axios';

const DEFAULT_AMOUNT_FCFA = 10000;

const PAYMENT_API_BASE = process.env.REACT_APP_PAYMENT_API_BASE || '';
const PAYMENT_MODE = process.env.REACT_APP_PAYMENT_MODE || 'mock';
const MOCK_SCENARIO_DEFAULT = process.env.REACT_APP_PAYMENT_MOCK_SCENARIO || 'success';

const MOOV_PREFIXES = (process.env.REACT_APP_MOOV_PREFIXES || '').split(',').map(s => s.trim()).filter(Boolean);
const AIRTEL_PREFIXES = (process.env.REACT_APP_AIRTEL_PREFIXES || '').split(',').map(s => s.trim()).filter(Boolean);

const buildHeaders = () => {
  const headers = {};
  const apiKey = process.env.REACT_APP_PAYMENT_PUBLIC_KEY;
  if (apiKey) headers['X-PAYMENT-PUBLIC-KEY'] = apiKey;
  return headers;
};

const normalizePhone = (value) => (value || '').replace(/[\s.-]/g, '');

const toLocal9 = (normalizedPlus241) => {
  if (!normalizedPlus241) return '';
  if (normalizedPlus241.startsWith('+241') && normalizedPlus241.length === 13) {
    return normalizedPlus241.slice(4);
  }
  return '';
};

export const validateGabonPhone = (raw) => {
  const phone = normalizePhone(raw);
  if (!phone) return { valid: false, normalized: '', error: 'Numéro requis' };

  // Accept: 9 digits local OR +241XXXXXXXXX
  const local9 = /^\d{9}$/;
  const intl = /^\+241\d{9}$/;

  if (local9.test(phone)) return { valid: true, normalized: `+241${phone}`, error: '' };
  if (intl.test(phone)) return { valid: true, normalized: phone, error: '' };

  return { valid: false, normalized: '', error: 'Numéro invalide (ex: 061234567 ou +241061234567)' };
};

export const validatePhoneForProvider = (provider, raw) => {
  const base = validateGabonPhone(raw);
  if (!base.valid) return base;

  const local = toLocal9(base.normalized);
  const prefix2 = local.slice(0, 2);

  const allowed = provider === 'MOOV_MONEY' ? MOOV_PREFIXES : provider === 'AIRTEL_MONEY' ? AIRTEL_PREFIXES : [];
  if (allowed.length === 0) {
    return base;
  }

  if (!allowed.includes(prefix2)) {
    return { valid: false, normalized: '', error: `Numéro non supporté pour ${provider === 'MOOV_MONEY' ? 'Moov Money' : 'Airtel Money'}` };
  }

  return base;
};

const lsGetInt = (key) => {
  try {
    const v = parseInt(localStorage.getItem(key) || '0', 10);
    return Number.isFinite(v) ? v : 0;
  } catch (_) {
    return 0;
  }
};

const lsSetInt = (key, v) => {
  try {
    localStorage.setItem(key, String(Math.max(0, parseInt(String(v || 0), 10) || 0)));
  } catch (_) {}
};

const parseJwtPayload = (token) => {
  try {
    const parts = String(token || '').split('.');
    if (parts.length < 2) return null;
    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`)
        .join('')
    );
    return JSON.parse(json);
  } catch (_) {
    return null;
  }
};

const userNamespace = () => {
  try {
    const token = localStorage.getItem('token') || '';
    const payload = parseJwtPayload(token);
    const uid = payload?.user_id || payload?.uid || payload?.id || payload?.sub || payload?.email || '';

    const safeUid = String(uid).replace(/[^a-zA-Z0-9]/g, '').slice(0, 32);
    if (safeUid) return `u_${safeUid}`;

    const safeTail = token.replace(/[^a-zA-Z0-9]/g, '').slice(-24);
    return safeTail ? `u_${safeTail}` : 'anon';
  } catch (_) {
    return 'anon';
  }
};

const creditKey = () => `immo_pub_credits_${userNamespace()}`;

const pendingKey = (scope) => `immo_payments_pending_${userNamespace()}_${scope}`;

const setPending = (scope, value) => {
  try {
    if (value) localStorage.setItem(pendingKey(scope), 'true');
    else localStorage.removeItem(pendingKey(scope));
  } catch (_) {}
};

const isPending = (scope) => {
  try {
    return localStorage.getItem(pendingKey(scope)) === 'true';
  } catch (_) {
    return false;
  }
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const paymentService = {
  amountFcfa: DEFAULT_AMOUNT_FCFA,

  getCredits() {
    return lsGetInt(creditKey());
  },

  addCredits(count = 1) {
    const current = lsGetInt(creditKey());
    lsSetInt(creditKey(), current + (parseInt(String(count), 10) || 0));
  },

  consumeCredit() {
    const current = lsGetInt(creditKey());
    if (current <= 0) return false;
    lsSetInt(creditKey(), current - 1);
    return true;
  },

  isPaymentPending(scope) {
    return isPending(scope);
  },

  async createPaymentIntent({ listingId, provider, phone, amountFcfa = DEFAULT_AMOUNT_FCFA, scenario }) {
    if (!provider) throw new Error('provider manquant');

    const scope = listingId ? `listing_${listingId}` : `credit_${provider}`;
    if (isPending(scope)) throw new Error('Paiement déjà en cours');

    const phoneCheck = validatePhoneForProvider(provider, phone);
    if (!phoneCheck.valid) throw new Error(phoneCheck.error);

    if (listingId && this.isListingPaid(listingId)) throw new Error('Déjà payé');

    setPending(scope, true);

    if (PAYMENT_MODE === 'mock' || !PAYMENT_API_BASE) {
      // Mock intent
      const mockScenario = scenario || MOCK_SCENARIO_DEFAULT;
      return {
        paymentId: `mock_${listingId}_${Date.now()}`,
        status: 'pending',
        provider,
        amountFcfa,
        phone: phoneCheck.normalized,
        scenario: mockScenario,
        createdAt: new Date().toISOString(),
      };
    }

    const resp = await axios.post(
      `${PAYMENT_API_BASE}/payments/intents`,
      {
        listing_id: listingId,
        provider,
        phone: phoneCheck.normalized,
        amount_fcfa: amountFcfa,
      },
      { headers: buildHeaders() }
    );

    return resp.data;
  },

  async getPaymentStatus({ paymentId, listingId, provider, scenario }) {
    if (!paymentId) throw new Error('paymentId manquant');

    const scope = listingId ? `listing_${listingId}` : `credit_${provider || 'unknown'}`;

    if (PAYMENT_MODE === 'mock' || !PAYMENT_API_BASE) {
      const mockScenario = scenario || MOCK_SCENARIO_DEFAULT;
      if (mockScenario === 'timeout') {
        await sleep(2500);
        setPending(scope, false);
        throw new Error('Timeout API');
      }
      if (mockScenario === 'failure') {
        await sleep(600);
        setPending(scope, false);
        return { paymentId, status: 'failure' };
      }
      if (mockScenario === 'pending') {
        await sleep(600);
        return { paymentId, status: 'pending' };
      }
      await sleep(600);
      setPending(scope, false);
      return { paymentId, status: 'success' };
    }

    const resp = await axios.get(`${PAYMENT_API_BASE}/payments/${paymentId}/status`, { headers: buildHeaders() });
    if (resp?.data?.status === 'success' || resp?.data?.status === 'failure') {
      setPending(scope, false);
    }
    return resp.data;
  },

  markListingAsPaid(listingId) {
    try {
      const key = `immo_payments_paid_${listingId}`;
      localStorage.setItem(key, 'true');
    } catch (_) {}
  },

  isListingPaid(listingId) {
    try {
      const key = `immo_payments_paid_${listingId}`;
      return localStorage.getItem(key) === 'true';
    } catch (_) {
      return false;
    }
  },
};
